import { collator } from "@/src/utils";
import uFuzzy from "@leeoniya/ufuzzy";
import { useDeferredValue, useMemo } from "react";

const fuzzer = new uFuzzy({
  compare: collator.compare,
  alpha: "a-zåäö",
  intraMode: 1,
});
export function nameMapper<T extends { name?: string | null }>(item: T) {
  return item.name ?? "";
}

export function useSearch<T>(
  _query: string,
  haystack: readonly T[],
  mapper: (item: T) => string
): [isSearching: boolean, [T, { part: string; matched: boolean }[]][]] {
  const query = useDeferredValue(_query);

  return useMemo(() => {
    if (!query || query.length === 0) {
      return [false, haystack.map((h) => [h, []] as const)];
    }
    const mappedHaystack = haystack.map(mapper);
    const [results, info] = fuzzer.search(mappedHaystack, query);

    if (!info) {
      return [
        true,
        results?.map((result) => [haystack[result], []] as const) ?? [],
      ] as const;
    }
    return [
      true,
      info.idx?.map((infoIdx) => [haystack[infoIdx], []] as const) ?? [],
    ] as const;
  }, [query, haystack, mapper]);
}

export function useFilterSearch<T>(
  _query: string,
  haystack: readonly T[],
  mapper: (item: T) => string
): [isSearching: boolean, readonly T[]] {
  const query = useDeferredValue(_query);

  return useMemo(() => {
    const latinizedQuery = uFuzzy.latinize(query);
    if (!latinizedQuery || latinizedQuery.length === 0) {
      return [false, haystack];
    }
    const mappedHaystack = haystack.map((s) => uFuzzy.latinize(mapper(s)));

    return [
      true,
      fuzzer
        .filter(mappedHaystack, latinizedQuery)
        ?.map((result) => haystack[result]) ?? [],
    ] as const;
  }, [query, haystack, mapper]);
}
